import { JSX, Suspense, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, Outlet, BrowserRouter, useLocation } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from "redux-persist/integration/react";
import { LinearProgress, Slide } from '@mui/material';
import { closeSnackbar, enqueueSnackbar, SnackbarProvider } from "notistack";
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { persistor, RootState, store } from './state/store';
import { SignalRProvider } from "./components/Providers/SignalRProvider";
import { GlobalModalProvider } from './components/Providers/GlobalModalProvider';
import Header from './components/Header/Header';
import Login from './pages/Login/Login';
import NotFound from "./pages/NotFound/NotFound";
import { API_BASE_URL } from "./config";
import { TimezoneProvider } from "./components/Providers/TimezoneProvider";
import useShiftBoundarySync from "./components/Hooks/useShiftBoundarySync";
import { UserDto } from "./DTOs/User/UserDto";
import { hasAllRoles, hasAnyRole } from './utilities/helpers/roleUtilities';
import { AppRouteConfig, createAppRoutes, SHIFT_RELATED_PAGE_PATHS } from "./utilities/appRoutes";
import { RoleConfig } from "./DTOs/Settings/RoleConfigDto";
import ShiftLoginBootstrapper from "./components/Providers/ShiftLoginBootstrapper";
import GlobalNotificationListener from './components/Providers/GlobalNotificationListener';
import ErrorScreen from './components/ErrorScreen/ErrorScreen';
import { useSignalRContext } from './components/Contexts/SignalRContext';
import { useApplyTheme } from './components/Hooks/useApplyTheme';
import { AppMuiProvider } from './components/Providers/MuiThemeProvider';
import ShiftLockBanner from './components/ShiftLockBanner/ShiftLockBanner';

interface ProtectedRouteProps {
  user: UserDto | null;
  access?: string[];
  children: JSX.Element;
  isAuthenticated?: boolean;
  accessAll?: string[];
}

function ProtectedRoute(
  {
    user,
    access,
    children,
    isAuthenticated,
    accessAll
  }: ProtectedRouteProps) {
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (access && !user) return <LinearProgress />;

  const hasPermission =
    (!access || hasAnyRole(user, access)) &&
    (!accessAll || hasAllRoles(user, accessAll));

  if (!hasPermission) {
    return <Navigate to="/" />;
  }

  return children;
}

function generateRoutes(routes: AppRouteConfig[], user: UserDto | null, isAuthenticated: boolean): JSX.Element[] {
  return routes.map((route, i) => {
    const element = route.protected
      ? (
        <ProtectedRoute
          key={route.path}
          user={user}
          access={route.access}
          isAuthenticated={isAuthenticated}
          accessAll={route.accessAll}
        >
          {route.element ?? <Outlet />}
        </ProtectedRoute>
      )
      : route.element ?? <Outlet />;

    return (
      <Route
        key={route.path || i}
        path={route.path}
        element={element}
      >
        {route.children && generateRoutes(route.children, user, isAuthenticated)}
      </Route>
    );
  });
}

function AppLayout() {
  useShiftBoundarySync();
  useApplyTheme();
  const { pathname } = useLocation();
  const isShiftPage = SHIFT_RELATED_PAGE_PATHS.has(pathname);
  const { isConnectionLoading, connectionError, isReconnecting } = useSignalRContext();
  const { isAuthenticated, isAuthLoading, error } = useSelector((state: RootState) => state.authReducer);

  const isMetaDataLoading = isAuthLoading || (isAuthenticated && isConnectionLoading);
  const errorMessage = error ?? null;
  const reconnectSnackbarId = useRef<string | number | null>(null);
  const disconnectedSnackbarId = useRef<string | number | null>(null);

  useEffect(() => {
    if (isReconnecting) {
      if (disconnectedSnackbarId.current !== null) {
        closeSnackbar(disconnectedSnackbarId.current);
        disconnectedSnackbarId.current = null;
      }
      reconnectSnackbarId.current = enqueueSnackbar(
        <div>
          Connection lost. Reconnecting <FontAwesomeIcon className="ml-2" icon={faSpinner} spin />
        </div>,
        {
          variant: "warning",
          anchorOrigin: { vertical: "top", horizontal: "center" },
          persist: true,
          preventDuplicate: true,
        }
      );
    } else {
      if (reconnectSnackbarId.current !== null) {
        closeSnackbar(reconnectSnackbarId.current);
        reconnectSnackbarId.current = null;
      }
      if (connectionError) {
        disconnectedSnackbarId.current = enqueueSnackbar(
          "Connection lost. Please refresh the page.",
          {
            variant: "error",
            anchorOrigin: { vertical: "top", horizontal: "center" },
            persist: true,
            preventDuplicate: true,
          }
        );
      } else if (disconnectedSnackbarId.current !== null) {
        closeSnackbar(disconnectedSnackbarId.current);
        disconnectedSnackbarId.current = null;
        enqueueSnackbar("Connection restored", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "center" },
          autoHideDuration: 3000,
        });
      }
    }
  }, [isReconnecting, connectionError]);

  return (
    <AppMuiProvider>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={(props) => <Slide {...props} direction="left" />}
        autoHideDuration={3000}
        dense
        preventDuplicate
      >
        <GlobalModalProvider>
          <div className="h-screen flex flex-col overflow-hidden">
            <Header />
            <ShiftLockBanner />
            <main className={`flex-1 overflow-y-auto bg-theme-bg transition-colors min-h-0 ${!isMetaDataLoading && isShiftPage ? 'pt-10 short:pt-7' : ''}`}> {/* Note: pt-10 reserves space for the shift-lock banner */}
              {isMetaDataLoading ? (
                <LinearProgress />
              ) : errorMessage ? (
                <ErrorScreen />
              ) : (
                <>
                  <GlobalNotificationListener />
                  <Suspense fallback={<LinearProgress />}>
                    <Outlet />
                  </Suspense>
                </>
              )}
            </main>
          </div>
        </GlobalModalProvider>
      </SnackbarProvider>
    </AppMuiProvider>
  );
}

// TODO: merge material type & class, buckets + bucket factors, Plant + Plant Buckets, pits and pit stages
function AppRouter({ isAuthenticated, user, roleConfig }: { isAuthenticated: boolean, user: UserDto | null, roleConfig: RoleConfig | null }) {
  const routes = useMemo(
    () => roleConfig ? createAppRoutes(roleConfig) : [],
    [roleConfig]
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={
          isAuthenticated
            ? <Navigate to="/" replace />
            : <Login />
        } />
        <Route path="/" element={
          <ProtectedRoute user={user} isAuthenticated={isAuthenticated}>
            <AppLayout />
          </ProtectedRoute>
        }>
          {generateRoutes(routes, user, isAuthenticated)}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

const App: React.FC = () => {
  const isAuthenticated = useSelector((state: RootState) => state.authReducer.isAuthenticated);
  const user = useSelector((state: RootState) => state.authReducer.user);
  const roleConfig = useSelector((state: RootState) => state.authReducer.roleConfig);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <TimezoneProvider user={user}>
          <SignalRProvider hubUrl={`${API_BASE_URL}/liveUpdateHub`}>
            <ShiftLoginBootstrapper />
            <AppRouter isAuthenticated={isAuthenticated} user={user} roleConfig={roleConfig} />
          </SignalRProvider>
        </TimezoneProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
